import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  Chip,
  ListItemText,
  TablePagination,
  LinearProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { Edge, Node } from '@xyflow/react';
import { apiGet, apiPut } from '../../api/client';
import type { Campaign, AudienceUser, CampaignContent, CampaignSettings, MailingChannel, Scenario } from '../../domain/mailingDomain';
import { campaignCanLaunch, campaignChannelsFromVisualNodes, normalizeSubscriberContactListIds } from '../../domain/mailingDomain';
import type { MailingCampaign } from '../mailing-constructor/mailingTypes';
import { fetchVisualMailings } from '../mailing-constructor/visualMailingsApi';
import {
  fetchSubscriberContactListsAll,
  fetchSubscriberListView,
  fieldChipsFromTitlesMap,
  extractSubscriberViewContactsPreview,
  readSubscriberViewListName,
  readSubscriberViewTotalContacts,
  readSubscriberViewTitlesMap,
  subscriberViewParamsDisplayRows,
  subscriberViewPaginationCount,
  type SubscriberContactListRow,
  type SubscriberListViewFieldChip,
} from '../../api/subscribersApi';
import { getSmsSubscribersManageUrl } from '../../auth/cascadeAuth';
import { useSmsPageDesign, smsPaperCardSx, smsEditorToolbarSx, SMS_PAGE } from '../../theme/smsPageDesign';
import { useTranslation } from 'react-i18next';

const SUBSCRIBER_VIEW_DEFAULT_ROWS_PER_PAGE = 10;
/** Первая загрузка: больше контактов в запросе, чтобы собрать все ключи полей (titles у провайдера иногда неполные). */
const SUBSCRIBER_VIEW_INITIAL_FETCH_COUNT = 100;
const SUBSCRIBER_VIEW_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100] as const;
const selectedSubscriberChipSx = {
  bgcolor: SMS_PAGE.accent,
  color: SMS_PAGE.text,
  border: `1px solid ${SMS_PAGE.accentHover}`,
  fontWeight: 700,
  '& .MuiChip-label': { px: 1.1 },
};

function emptyChannelBlock(ch: MailingChannel): NonNullable<CampaignContent[MailingChannel]> {
  const base = { title: '', body: '', sendingConfigured: false };
  switch (ch) {
    case 'push':
      return { ...base, imageUrl: '', actionUrl: '' };
    case 'sms':
      return { ...base, senderName: '' };
    case 'email':
      return { ...base, preheader: '' };
    case 'viber':
      return { ...base, buttonText: '', buttonUrl: '' };
    case 'waba':
      return { ...base, templateName: '', languageCode: '' };
    default:
      return base;
  }
}

function defaultContent(): CampaignContent {
  const c: CampaignContent = {};
  const chs: MailingChannel[] = ['push', 'sms', 'email', 'viber', 'waba'];
  for (const ch of chs) {
    c[ch] = emptyChannelBlock(ch);
  }
  return c;
}

function mergeContent(base: CampaignContent | undefined): CampaignContent {
  const out = defaultContent();
  if (!base) return out;
  const chs: MailingChannel[] = ['push', 'sms', 'email', 'viber', 'waba'];
  for (const ch of chs) {
    const b = base[ch];
    if (b && typeof b === 'object') {
      out[ch] = { ...emptyChannelBlock(ch), ...b };
    }
  }
  return out;
}

export type CampaignFormPanelHandle = {
  /** Сохранить кампанию (как кнопка «Сохранить» в панели). `false` — валидация или загрузка. */
  save: () => Promise<boolean>;
};

export type CampaignFormPanelProps = {
  campaignEditId: string;
  scenarios: Scenario[];
  onSaved: () => void;
};

const CampaignFormPanel = forwardRef<CampaignFormPanelHandle, CampaignFormPanelProps>(function CampaignFormPanel(
  { campaignEditId, scenarios, onSaved },
  ref
) {
  const { t } = useTranslation();
  const { theme, isSmsLight } = useSmsPageDesign();

  const [name, setName] = useState('');
  const [content, setContent] = useState<CampaignContent>(() => defaultContent());
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<CampaignSettings>({});
  const [visualMailings, setVisualMailings] = useState<MailingCampaign[]>([]);
  const [audienceEdit, setAudienceEdit] = useState<AudienceUser[]>([]);
  const [subscriberListsRows, setSubscriberListsRows] = useState<SubscriberContactListRow[]>([]);
  const [subscriberListsErr, setSubscriberListsErr] = useState<string | null>(null);
  const [subscriberListMenuSearch, setSubscriberListMenuSearch] = useState('');
  const [subscriberListFields, setSubscriberListFields] = useState<{
    loading: boolean;
    err: string | null;
    sections: Array<{
      listId: string;
      page: number;
      rowsPerPage: number;
      sectionLoading: boolean;
      fields: SubscriberListViewFieldChip[];
      titlesMap: Record<string, string>;
      contactPreview: {
        rows: Array<{ id: string; params: Record<string, string> }>;
        pageContactCount: number;
      };
      totalContacts?: number;
      responseListName?: string;
      loadError?: string;
    }>;
  }>({ loading: false, err: null, sections: [] });

  /** Отменяет применение устаревших ответов при быстрой смене страницы. */
  const subscriberViewReqGenRef = useRef<Record<string, number>>({});

  const locked = Boolean(campaign?.locked);
  const cabinetAborted = campaign?.status === 'aborted';
  const formDisabled = locked || cabinetAborted;

  const savedSubscriberListIds = useMemo(
    () => normalizeSubscriberContactListIds(campaign?.settings ?? null),
    [campaign?.settings]
  );

  const subscriberListImmutable = savedSubscriberListIds.length > 0;

  const draftSubscriberListIds = useMemo(() => normalizeSubscriberContactListIds(settings), [settings]);

  const filteredSubscriberListsForMenu = useMemo(() => {
    const q = subscriberListMenuSearch.trim().toLowerCase();
    if (!q) return subscriberListsRows;
    return subscriberListsRows.filter((row) => {
      const name = String(row.name ?? '').toLowerCase();
      const id = String(row.contactListId);
      const cnt = row.contactsCount != null ? String(row.contactsCount) : '';
      return name.includes(q) || id.includes(q) || (cnt && cnt.includes(q));
    });
  }, [subscriberListsRows, subscriberListMenuSearch]);

  const numericSubscriberListIdsForFields = useMemo(() => {
    const raw = subscriberListImmutable ? savedSubscriberListIds : draftSubscriberListIds;
    const nums = raw
      .map((x) => parseInt(String(x), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    return Array.from(new Set(nums)).sort((a, b) => a - b);
  }, [subscriberListImmutable, savedSubscriberListIds, draftSubscriberListIds]);

  const settingsForApi = useMemo((): CampaignSettings => {
    const base =
      settings && typeof settings === 'object' ? { ...settings } : ({} as CampaignSettings);
    if (savedSubscriberListIds.length) {
      return {
        ...base,
        subscriberContactListIds: [...savedSubscriberListIds],
        subscriberContactListId: savedSubscriberListIds[0],
      };
    }
    const ids = normalizeSubscriberContactListIds(settings);
    if (!ids.length) {
      const next = { ...base } as CampaignSettings & Record<string, unknown>;
      delete next.subscriberContactListIds;
      delete next.subscriberContactListId;
      return next as CampaignSettings;
    }
    return {
      ...base,
      subscriberContactListIds: ids,
      subscriberContactListId: ids[0],
    };
  }, [settings, savedSubscriberListIds]);

  const immutableSubscriberListLabels = useMemo(() => {
    return savedSubscriberListIds.map((sid) => {
      const row = subscriberListsRows.find((r) => String(r.contactListId) === sid);
      if (row) return row.contactsCount != null ? `${row.name} (${row.contactsCount})` : row.name;
      return `#${sid}`;
    });
  }, [savedSubscriberListIds, subscriberListsRows]);

  const linkedVisual = useMemo(
    () => (settings.visualMailingId ? visualMailings.find((v) => v.id === settings.visualMailingId) : undefined),
    [settings.visualMailingId, visualMailings]
  );

  const visualGraphChannels = useMemo(() => {
    const m = linkedVisual;
    if (!m?.nodes?.length) return [] as MailingChannel[];
    return campaignChannelsFromVisualNodes(m.nodes as Array<{ type?: string; data?: unknown }>);
  }, [linkedVisual]);

  const campaignForCheck: Campaign | null = useMemo(() => {
    if (!campaign) return null;
    return {
      ...campaign,
      name,
      scenarioId: null,
      content,
      audience: audienceEdit,
      /** Локальные настройки формы; иначе проверка запуска видит только сохранённый снимок. */
      settings: { ...(campaign.settings && typeof campaign.settings === 'object' ? campaign.settings : {}), ...settings },
      updatedAt: Date.now(),
    };
  }, [campaign, content, name, audienceEdit, settings]);

  const launchCheck = campaignForCheck
    ? campaignCanLaunch(campaignForCheck, null, {
        visualGraphChannels,
        visualNodes: linkedVisual?.nodes as Node[] | undefined,
        visualEdges: linkedVisual?.edges as Edge[] | undefined,
      })
    : { ok: false, reason: '—' };

  const loadExisting = useCallback(() => {
    setLoading(true);
    apiGet<Campaign>(`/api/campaigns/${campaignEditId}`)
      .then((c) => {
        setCampaign(c);
        setName(c.name);
        setContent(mergeContent(c.content));
        const base = c.settings && typeof c.settings === 'object' ? { ...c.settings } : {};
        const rawSettings = { ...base } as CampaignSettings;
        if ((base as Record<string, unknown>).audienceInputMode === 'json') {
          delete (rawSettings as Record<string, unknown>).audienceInputMode;
        }
        setSettings(rawSettings);
        const aud = Array.isArray(c.audience) ? c.audience : [];
        setAudienceEdit(aud);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [campaignEditId, scenarios]);

  useEffect(() => {
    setErr(null);
    loadExisting();
  }, [campaignEditId, loadExisting]);

  useEffect(() => {
    let cancelled = false;
    void fetchVisualMailings()
      .then((list) => {
        if (!cancelled) setVisualMailings(list);
      })
      .catch(() => {
        if (!cancelled) setVisualMailings([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let c = false;
    setSubscriberListsErr(null);
    void fetchSubscriberContactListsAll()
      .then((rows) => {
        if (!c) setSubscriberListsRows(rows);
      })
      .catch((e: Error) => {
        if (!c) setSubscriberListsErr(e.message);
      });
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    const ids = numericSubscriberListIdsForFields;
    subscriberViewReqGenRef.current = {};
    if (!ids.length) {
      setSubscriberListFields({ loading: false, err: null, sections: [] });
      return;
    }
    let cancelled = false;
    const displayRp = SUBSCRIBER_VIEW_DEFAULT_ROWS_PER_PAGE;
    const fetchCount = Math.max(displayRp, SUBSCRIBER_VIEW_INITIAL_FETCH_COUNT);
    setSubscriberListFields({ loading: true, err: null, sections: [] });
    void Promise.allSettled(
      ids.map((id) => fetchSubscriberListView(id, { count: fetchCount, offset: 0 })),
    ).then(
      (results) => {
        if (cancelled) return;
        let firstErr: string | null = null;
        const sections = ids.map((id, i) => {
          const r = results[i];
          if (r.status === 'rejected') {
            const msg = (r.reason as Error)?.message ?? String(r.reason);
            if (!firstErr) firstErr = msg;
            return {
              listId: String(id),
              page: 0,
              rowsPerPage: displayRp,
              sectionLoading: false,
              fields: [] as SubscriberListViewFieldChip[],
              titlesMap: {},
              contactPreview: { rows: [], pageContactCount: 0 },
              loadError: msg,
            };
          }
          const body = r.value as unknown;
          const titlesMap = readSubscriberViewTitlesMap(body);
          return {
            listId: String(id),
            page: 0,
            rowsPerPage: displayRp,
            sectionLoading: false,
            fields: fieldChipsFromTitlesMap(titlesMap),
            titlesMap,
            contactPreview: extractSubscriberViewContactsPreview(body, displayRp),
            totalContacts: readSubscriberViewTotalContacts(body),
            responseListName: readSubscriberViewListName(body),
            loadError: undefined,
          };
        });
        const allFailed = sections.length > 0 && sections.every((s) => s.loadError);
        setSubscriberListFields({ loading: false, err: allFailed ? firstErr : null, sections });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [numericSubscriberListIdsForFields]);

  const loadSubscriberViewPage = useCallback((listId: string, page: number, rowsPerPage: number) => {
    const idNum = parseInt(listId, 10);
    if (!Number.isFinite(idNum) || idNum < 1) return;
    const nextGen = (subscriberViewReqGenRef.current[listId] ?? 0) + 1;
    subscriberViewReqGenRef.current[listId] = nextGen;
    const gen = nextGen;
    setSubscriberListFields((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.listId === listId ? { ...s, sectionLoading: true, loadError: undefined } : s,
      ),
    }));
    void fetchSubscriberListView(idNum, {
      count: rowsPerPage,
      offset: page * rowsPerPage,
    })
      .then((body) => {
        if (subscriberViewReqGenRef.current[listId] !== gen) return;
        setSubscriberListFields((prev) => ({
          ...prev,
          sections: prev.sections.map((s) =>
            s.listId !== listId
              ? s
              : (() => {
                  const titlesMap = { ...s.titlesMap, ...readSubscriberViewTitlesMap(body) };
                  return {
                    listId,
                    page,
                    rowsPerPage,
                    sectionLoading: false,
                    loadError: undefined,
                    titlesMap,
                    fields: fieldChipsFromTitlesMap(titlesMap),
                    contactPreview: extractSubscriberViewContactsPreview(body),
                    totalContacts: readSubscriberViewTotalContacts(body),
                    responseListName: readSubscriberViewListName(body),
                  };
                })(),
          ),
        }));
      })
      .catch((e: Error) => {
        if (subscriberViewReqGenRef.current[listId] !== gen) return;
        setSubscriberListFields((prev) => ({
          ...prev,
          sections: prev.sections.map((s) =>
            s.listId === listId ? { ...s, sectionLoading: false, loadError: e.message } : s,
          ),
        }));
      });
  }, []);

  const persistCampaign = useCallback(async (): Promise<boolean> => {
    if (loading) return false;
    if (cabinetAborted) return false;
    setErr(null);
    if (locked && campaign) {
      try {
        await apiPut<Campaign>(`/api/campaigns/${campaign.id}`, { settings: settingsForApi });
        loadExisting();
        onSaved();
        return true;
      } catch (e) {
        setErr((e as Error).message);
        return false;
      }
    }
    if (!campaign) return false;
    const body = {
      name,
      scenarioId: null,
      audience: audienceEdit,
      content,
      settings: settingsForApi,
    };
    try {
      await apiPut<Campaign>(`/api/campaigns/${campaignEditId}`, body);
      loadExisting();
      onSaved();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      return false;
    }
  }, [
    loading,
    cabinetAborted,
    locked,
    campaign,
    settingsForApi,
    name,
    audienceEdit,
    content,
    campaignEditId,
    loadExisting,
    onSaved,
  ]);

  useImperativeHandle(ref, () => ({ save: () => persistCampaign() }), [persistCampaign]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 'none',
        mx: 0,
        pb: 2,
        pt: 0,
        px: 0,
      }}
    >
      <Box sx={smsEditorToolbarSx(isSmsLight)}>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={formDisabled}
          variant="standard"
          placeholder={t('campaignForm.name')}
          aria-label={t('campaignForm.name')}
          sx={{
            flex: 1,
            minWidth: 120,
            maxWidth: '100%',
            '& .MuiInputBase-root': { fontWeight: 600, fontSize: '1rem' },
            '& .MuiInputBase-input::placeholder': { opacity: 0.5, fontWeight: 500 },
          }}
        />
      </Box>

      {err && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {err}
        </Alert>
      )}
      {cabinetAborted && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('campaignForm.cabinetAbortedAlert')}
        </Alert>
      )}
      {locked && !cabinetAborted && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('campaignForm.lockedAlert')}
        </Alert>
      )}

      {!launchCheck.ok && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('campaignForm.launchBlocked', { reason: launchCheck.reason })}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2, mt: 2, ...smsPaperCardSx(theme, isSmsLight) }}>
        {!linkedVisual ? (
          <Typography variant="body2" color="text.secondary">
            {t('campaignSettings.noLinkedVisual')}
          </Typography>
        ) : null}
        {subscriberListsErr ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('campaignForm.subscriberListLoadError')}: {subscriberListsErr}
          </Alert>
        ) : null}
        {subscriberListImmutable ? (
          <>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>
              {t('campaignForm.subscriberListLabel')}
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
              {immutableSubscriberListLabels.map((label, i) => (
                <Typography
                  key={`${savedSubscriberListIds[i]}-${i}`}
                  component="li"
                  variant="body2"
                  sx={{ color: 'text.primary' }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          </>
        ) : (
          <FormControl fullWidth size="small" sx={{ mt: 2 }} disabled={formDisabled}>
            <InputLabel id="campaign-subscriber-list-lbl" shrink>
              {t('campaignForm.subscriberListLabel')}
            </InputLabel>
            <Select
              labelId="campaign-subscriber-list-lbl"
              label={t('campaignForm.subscriberListLabel')}
              multiple
              value={draftSubscriberListIds}
              onChange={(e) => {
                const v = e.target.value;
                const nextIds =
                  typeof v === 'string' ? (v.split(',') as string[]) : ([...(v as string[])] as string[]);
                setSettings((prev) => {
                  const next = { ...prev };
                  if (!nextIds.length) {
                    delete next.subscriberContactListIds;
                    delete next.subscriberContactListId;
                  } else {
                    next.subscriberContactListIds = nextIds.map(String);
                    next.subscriberContactListId = String(nextIds[0]);
                  }
                  return next;
                });
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((sid) => {
                    const row = subscriberListsRows.find((r) => String(r.contactListId) === sid);
                    const label =
                      row != null
                        ? row.contactsCount != null
                          ? `${row.name} (${row.contactsCount})`
                          : row.name
                        : sid;
                    return <Chip key={sid} label={label} size="small" sx={selectedSubscriberChipSx} />;
                  })}
                </Box>
              )}
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 360 } },
              }}
              onClose={() => {
                setSubscriberListMenuSearch('');
              }}
            >
              <ListSubheader
                sx={{
                  lineHeight: 'normal',
                  px: 1,
                  py: 1,
                  bgcolor: 'background.paper',
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('campaignForm.subscriberListSearchPlaceholder')}
                  value={subscriberListMenuSearch}
                  onChange={(e) => setSubscriberListMenuSearch(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ mt: 0.5 }}
                />
              </ListSubheader>
              {filteredSubscriberListsForMenu.length === 0 ? (
                <ListSubheader sx={{ color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {t('campaignForm.subscriberListSearchEmpty')}
                </ListSubheader>
              ) : (
                filteredSubscriberListsForMenu.map((row) => {
                  const sid = String(row.contactListId);
                  const checked = draftSubscriberListIds.includes(sid);
                  return (
                    <MenuItem key={row.contactListId} value={sid}>
                      <Checkbox checked={checked} size="small" sx={{ mr: 0.5, py: 0.25 }} />
                      <ListItemText
                        primary={row.contactsCount != null ? `${row.name} (${row.contactsCount})` : row.name}
                      />
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
        )}
        {!subscriberListImmutable ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('campaignForm.subscriberListHint')}
          </Typography>
        ) : null}
        <Button
          component="a"
          href={getSmsSubscribersManageUrl()}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
          sx={{ mt: 1.25, textTransform: 'none', alignSelf: 'flex-start' }}
        >
          {t('campaignForm.subscriberListsManageLink')}
        </Button>

        {subscriberListFields.err ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('campaignForm.subscriberListFieldsError')}: {subscriberListFields.err}
          </Alert>
        ) : null}
        {!subscriberListFields.loading && numericSubscriberListIdsForFields.length > 0
          ? subscriberListFields.sections.map((sec, idx) => {
              return (
                <Box key={sec.listId} sx={{ mt: idx === 0 ? 1 : 1.75 }}>
                  {sec.loadError ? (
                    <Alert severity="warning" sx={{ mt: 0.75, py: 0.5 }}>
                      {t('campaignForm.subscriberListFieldsError')}: {sec.loadError}
                    </Alert>
                  ) : null}
                  {!sec.loadError && sec.contactPreview.pageContactCount === 0 ? (
                    sec.totalContacts != null && sec.totalContacts > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('campaignForm.subscriberListContactsMissingInResponse', { count: sec.totalContacts })}
                      </Typography>
                    ) : sec.fields.length > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('campaignForm.subscriberListContactsPreviewEmpty')}
                      </Typography>
                    ) : null
                  ) : null}
                  {!sec.loadError && subscriberViewPaginationCount(sec) > 0 ? (
                    <>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="text.primary"
                        sx={{ display: 'block', mt: 1.25 }}
                      >
                        {t('campaignForm.subscriberListContactsPreviewTitle')}
                      </Typography>
                      <Box sx={{ position: 'relative', mt: 0.75 }}>
                        {sec.contactPreview.pageContactCount === 0 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
                            {t('campaignForm.subscriberListContactsEmptyPage')}
                          </Typography>
                        ) : (
                          <>
                            {sec.sectionLoading ? (
                              <LinearProgress
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  top: -2,
                                  zIndex: 1,
                                  height: 2,
                                  borderRadius: '2px',
                                }}
                              />
                            ) : null}
                            <Box sx={{ opacity: sec.sectionLoading ? 0.55 : 1, transition: 'opacity 0.2s' }}>
                              {sec.contactPreview.rows.map((row) => {
                                const paramRows = subscriberViewParamsDisplayRows(row.params, sec.titlesMap);
                                return (
                                  <Paper
                                    key={`${sec.listId}-c-${sec.page}-${row.id}`}
                                    elevation={0}
                                    variant="outlined"
                                    sx={{ mt: 1, p: 1.25, borderColor: 'divider', borderRadius: 1 }}
                                  >
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                                      {t('campaignForm.subscriberListContactRecord', { id: row.id })}
                                    </Typography>
                                    {paramRows.length === 0 ? (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        {t('campaignForm.subscriberListContactParamsEmpty')}
                                      </Typography>
                                    ) : (
                                      <Box
                                        component="dl"
                                        sx={{
                                          m: 0,
                                          mt: 0.6,
                                          display: 'grid',
                                          gridTemplateColumns: { xs: '1fr', sm: 'minmax(112px, 160px) 1fr' },
                                          columnGap: 1.5,
                                          rowGap: 0.35,
                                          '& dd': { m: 0 },
                                        }}
                                      >
                                        {paramRows.map((pr) => (
                                          <React.Fragment key={`${sec.listId}-${row.id}-${pr.key}`}>
                                            <Typography
                                              component="dt"
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {pr.label}
                                            </Typography>
                                            <Typography
                                              component="dd"
                                              variant="caption"
                                              sx={{ color: 'text.primary', wordBreak: 'break-word' }}
                                            >
                                              {pr.value.trim() ? pr.value : '—'}
                                            </Typography>
                                          </React.Fragment>
                                        ))}
                                      </Box>
                                    )}
                                  </Paper>
                                );
                              })}
                            </Box>
                          </>
                        )}
                      </Box>
                      <TablePagination
                          component="div"
                          sx={{
                            px: { xs: 0, sm: 0 },
                            '& .MuiTablePagination-toolbar': {
                              px: { xs: 0, sm: 1 },
                              minHeight: 44,
                              flexWrap: 'wrap',
                              gap: 0.5,
                            },
                            '& .MuiTablePagination-displayedRows, & .MuiTablePagination-actions': {
                              margin: { xs: 0, sm: undefined },
                            },
                            '& .MuiTablePagination-actions .MuiIconButton-root': {
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                            },
                            '& .MuiTablePagination-actions .Mui-disabled': { bgcolor: 'transparent' },
                          }}
                          rowsPerPageOptions={[...SUBSCRIBER_VIEW_ROWS_PER_PAGE_OPTIONS]}
                          count={subscriberViewPaginationCount(sec)}
                          page={sec.page}
                          rowsPerPage={sec.rowsPerPage}
                          onPageChange={(_, nextPage) => {
                            loadSubscriberViewPage(sec.listId, nextPage, sec.rowsPerPage);
                          }}
                          onRowsPerPageChange={(e) => {
                            const rp = parseInt(String(e.target.value), 10);
                            if (!Number.isFinite(rp) || rp <= 0) return;
                            loadSubscriberViewPage(sec.listId, 0, rp);
                          }}
                          labelRowsPerPage={t('campaignForm.subscriberListRowsPerPage')}
                          labelDisplayedRows={({ from, to, count }) =>
                            t('campaignForm.subscriberListPaginationRange', { from, to, count })
                          }
                        />
                    </>
                  ) : null}
                </Box>
              );
            })
          : null}
      </Paper>
    </Box>
  );
});

export default CampaignFormPanel;
